/**
 * RetirePlanModal — the confirm dialog for RETIRING a plan (MANAGEMENT 14 pricing). DELETE
 * /management/pricing/plans/:code is a SOFT supersede (actif=false + deleted_at; never a hard delete), so
 * old SubscriptionPayment.plan_code refs survive and the plan can be restored later (L-1). This modal only
 * confirms the intent (no editable fields); the restore inverse is a direct one-click action on the card.
 *
 * AA: a clear warning body, role="alert" on the error, a danger-variant confirm. FR i18n.
 */
import { useState } from "react";
import { Button, Modal, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { useRetirePlan } from "../screens/pricing.api";
import type { PlanRow } from "../screens/pricing.types";

export interface RetirePlanModalProps {
  plan: PlanRow | undefined;
  onClose: () => void;
}

export function RetirePlanModal({ plan, onClose }: RetirePlanModalProps) {
  const { t } = useT();
  const toast = useToast();
  const open = plan !== undefined;
  const retire = useRetirePlan();
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    retire.mutate(
      { code: plan!.code },
      {
        onSuccess: () => {
          toast.success(t("mgmt.pricing.retired"));
          onClose();
        },
        onError: (err) => setError(err instanceof ApiError ? err.message : t("mgmt.pricing.error")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={open ? t("mgmt.pricing.retire.title", { code: plan!.code }) : ""}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("mgmt.pricing.cancel")}
          </Button>
          <Button variant="danger" onClick={submit} loading={retire.isPending}>
            {t("mgmt.pricing.retire.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{t("mgmt.pricing.retire.warn")}</p>
        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
